import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Save, FileText, RotateCcw, AlertTriangle, Eye, History } from 'lucide-react';

interface Snapshot {
  id: string;
  archived_at: string;
  archived_by_email: string | null;
  change_summary: string;
  changed_keys: string[];
}

interface SectionMeta { key: string; group: string; title: string; default: string; }
interface WordingsPayload { insurer_id: string; sections: Record<string, string>; section_meta: SectionMeta[]; updated_at?: string; updated_by?: string; }

const GROUP_ORDER = ['Front matter', 'General', 'Special risks', 'Sections', 'Disclosures'];

export const PolicyWordingsPage: React.FC = () => {
  const [data, setData] = useState<WordingsPayload | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<Snapshot[]>([]);
  const [changeSummary, setChangeSummary] = useState('');

  const load = async () => {
    try {
      const r = await api.get<WordingsPayload>('/policy-wordings');
      setData(r.data);
      setDrafts({ ...r.data.sections });
    } catch { toast.error('Failed to load policy wordings'); }
  };
  const loadHistory = async () => {
    try {
      const r = await api.get<Snapshot[]>('/policy-wordings/history');
      setHistory(r.data);
    } catch (err) { console.warn('policy-wordings history load failed', err); }
  };
  // Mount-once loads: `load()` and `loadHistory()` are stable module-scope
  // fetchers that only update state via setState — no reactive deps needed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); void loadHistory(); }, []);

  const dirtyKeys = useMemo(() => {
    if (!data) return [];
    return Object.keys(drafts).filter((k) => (drafts[k] ?? '') !== (data.sections[k] ?? ''));
  }, [drafts, data]);

  const save = async () => {
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    try {
      const payload: Record<string, string> = {};
      dirtyKeys.forEach((k) => { payload[k] = drafts[k]; });
      await api.put('/policy-wordings', { sections: payload, change_summary: changeSummary || undefined });
      toast.success(`Saved ${dirtyKeys.length} wording section${dirtyKeys.length === 1 ? '' : 's'}. Snapshot archived so you can roll back at any time.`);
      setChangeSummary('');
      await load();
      await loadHistory();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const previewPdf = async () => {
    try {
      const token = (await import('@/lib/api')).getAccessToken();
      const r = await fetch(`${(await import('@/lib/api')).API_BASE}/policy-wordings/preview.pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ sections: drafts }),
      });
      if (!r.ok) throw new Error('Preview failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch { toast.error('Failed to render preview'); }
  };

  const rollback = async (snapId: string) => {
    if (!confirm('Roll back all wordings to this snapshot? The current state will itself be archived as a new snapshot first.')) return;
    try {
      await api.post(`/policy-wordings/history/${snapId}/rollback`, {});
      toast.success('Rolled back. Reload to see the restored wordings.');
      await load();
      await loadHistory();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Rollback failed';
      toast.error(msg);
    }
  };

  const reset = (k: string) => {
    if (!data) return;
    const meta = data.section_meta.find((m) => m.key === k);
    if (meta) setDrafts((d) => ({ ...d, [k]: meta.default }));
  };

  if (!data) return <div className="text-raptor-navy/50 text-center py-10">Loading policy wordings…</div>;

  const grouped: Record<string, SectionMeta[]> = {};
  data.section_meta.forEach((m) => { grouped[m.group] = grouped[m.group] || []; grouped[m.group].push(m); });

  return (
    <div className="space-y-4 animate-fade-in" data-testid="policy-wordings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-headline text-2xl text-raptor-navy tracking-wide">Policy wordings</h2>
          <p className="text-sm text-raptor-navy/60 font-body max-w-3xl">
            Editable boilerplate that appears on every generated Full Policy Document — note to policyholder, general clauses, warranties, claims procedures, disclosures, ombud &amp; regulator particulars, sharing consent, SASRIA. Changes only apply to <b>newly issued / renewed / addended</b> policies from the moment you save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="blue"><FileText size={10} /> {data.section_meta.length} sections</Badge>
          {dirtyKeys.length > 0 && <Badge variant="warning"><AlertTriangle size={10} /> {dirtyKeys.length} unsaved</Badge>}
        </div>
      </div>

      {GROUP_ORDER.filter((g) => grouped[g]).map((g) => (
        <Card key={g} data-testid={`wordings-group-${g.replace(/\s+/g, '-').toLowerCase()}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-raptor-blue font-bold">{g}</span>
            </CardTitle>
            <CardDescription>{g === 'Disclosures' ? 'Legally-required regulatory disclosures (S21 Code of Conduct).' : g === 'General' ? 'Cross-policy conditions, warranties, exclusions and claims.' : g === 'Front matter' ? 'Introductory boilerplate for the policyholder.' : g === 'Special risks' ? 'SASRIA and special-risk cover text.' : 'Section-specific operative wording pointer.'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {grouped[g].map((m) => {
              const dirty = (drafts[m.key] ?? '') !== (data.sections[m.key] ?? '');
              return (
                <div key={m.key} data-testid={`wording-${m.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-semibold text-raptor-navy">{m.title}</label>
                    <div className="flex items-center gap-2">
                      {dirty && <Badge variant="warning">edited</Badge>}
                      <Button variant="ghost" size="sm" onClick={() => reset(m.key)} title="Reset to system default">
                        <RotateCcw size={12} className="mr-1" /> Reset
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    rows={Math.max(3, Math.min(14, ((drafts[m.key] ?? '').split('\n').length + 1)))}
                    value={drafts[m.key] ?? ''}
                    onChange={(e) => setDrafts({ ...drafts, [m.key]: e.target.value })}
                    className="font-body text-sm"
                    data-testid={`wording-input-${m.key}`}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {/* Sticky save bar */}
      <div className="sticky bottom-0 -mx-6 -mb-6 border-t border-border/60 bg-white/95 backdrop-blur px-6 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="wordings-save-bar">
        <div className="text-xs text-raptor-navy/70">
          {dirtyKeys.length === 0 ? <>All sections match saved copy.</> : <>{dirtyKeys.length} unsaved section{dirtyKeys.length === 1 ? '' : 's'}: <span className="text-raptor-blue">{dirtyKeys.slice(0, 3).join(', ')}</span>{dirtyKeys.length > 3 ? '…' : ''}</>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            className="h-8 text-xs border border-border/70 rounded-md px-2 w-56 font-body"
            placeholder="What changed? (optional summary)"
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            data-testid="wordings-change-summary"
          />
          <Button variant="outline" onClick={() => void previewPdf()} disabled={saving} data-testid="wordings-preview-btn">
            <Eye size={14} className="mr-1" /> Preview PDF
          </Button>
          <Button variant="outline" onClick={() => setDrafts({ ...data.sections })} disabled={dirtyKeys.length === 0 || saving} data-testid="wordings-discard">Discard</Button>
          <Button variant="gradient" onClick={() => void save()} disabled={dirtyKeys.length === 0 || saving} data-testid="wordings-save-btn">
            <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PolicyWordingsPage;
